import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {Check, Lock} from '../../../icons';
import {toast} from 'react-toastify';
import {copyPlainText} from '../../../../utils/copyPlainText';
import {promptAskPhrase} from '../../../../utils/promptAskPhrase';
import {askPhraseToastContent} from '../../../agency/prompts/askPhraseToast';
import {isAgencyPrompt} from './PromptFilters';

/**
 * La tarjeta del prompt sugerido del cierre del Recorrido, las filas de los prompts de la agencia
 * y las tarjetas de Dashbo — y la plomería de copiado y de orden que comparten.
 *
 * Nada de esto pide datos propios: todo sale del payload que la banda YA pide
 * (`agent/prompt-library/`).
 *
 * Por qué el ranking y el copiado viven acá y no en un archivo por superficie: las tres hacen lo
 * mismo al hacer clic —clipboard + ping de uso— y el sugerido y la lista propia comparten criterio
 * (usos, y en un empate el orden de la lista). Duplicarlo garantizaba que en algún momento una
 * superficie pingueara y la otra no, o que los dos rankings divergieran.
 *
 * **Dos cosas se copian, y no son lo mismo.** «Copiar pedido» copia la FRASE con la que se le pide
 * el prompt a la IA (`promptAskPhrase`): el texto sigue viviendo en Dashbo y la IA trae siempre la
 * última versión que editó el equipo. «Copiar el texto» copia el prompt entero, para quien prefiere
 * pegarlo — y ese chat queda con su copia, que la próxima edición no alcanza.
 */

const COPIED_MS = 2000;

export const COPY_ASK = 'ask';
export const COPY_TEXT = 'text';

/** Usos de un prompt, tolerante a un `social` ausente o corrupto (0 es el piso, nunca `NaN`). */
export function promptUses(prompt) {
    const uses = prompt && prompt.social ? prompt.social.uses : 0;
    return typeof uses === 'number' && uses > 0 ? uses : 0;
}

/** ¿Este prompt está habilitado para este cliente? Sin `lock` (creado recién, o un backend viejo)
 *  se asume que sí: el bloqueo es una afirmación que el payload tiene que hacer explícita. */
export function isPromptEnabled(prompt) {
    return !prompt || !prompt.lock || prompt.lock.enabled !== false;
}

// El cliente en las dos convenciones que resuelve el Backend: `{cliente}` (biblioteca) y
// `{{client_name}}` / `{{client_id}}` (matriz del analista).
const CLIENT_PLACEHOLDER_RE = /\{cliente\}|\{\{client_(?:name|id)\}\}/;

/** ¿El texto CRUDO de un prompt habla de un cliente? Se pregunta antes de resolverlo. */
export function textNeedsClient(text) {
    return CLIENT_PLACEHOLDER_RE.test(String(text || ''));
}

/**
 * ¿El pedido de este prompt fija el cliente de la banda? Los de un cliente, siempre. Los de toda
 * la agencia, sólo si su texto lo pide (`needs_client`, que marca `BoosterUseIaBand` sobre el texto
 * CRUDO: a las filas ya les llega resuelto). Un prompt de cartera («Recorre mis clientes uno por
 * uno», review C-03 del 04/10) no trae `{cliente}`: fijarle uno lo convertiría en otro prompt.
 */
export function askPinsClient(prompt) {
    return !!prompt && (prompt.scope === 'CLIENT' || prompt.needs_client === true);
}

/**
 * Orden por uso descendente. La copia con `slice()` es a propósito: `sort` muta, y el array que
 * llega es el state de la banda. El desempate es el ORDEN DE ENTRADA —para los de Dashbo, el del
 * payload, que ya viene con los habilitados primero y la curaduría— porque `sort` es estable desde
 * ES2019.
 */
export function byUsesDesc(prompts) {
    return (prompts || []).slice().sort((a, b) => promptUses(b) - promptUses(a));
}

// Los de ESTE cliente van antes que los de toda la agencia: son los que alguien escribió pensando
// en él, y los de toda la agencia los ve igual en cualquier otro cliente.
const OWN_SCOPE_RANK = {CLIENT: 0, AGENCY: 1};

function compareTitles(a, b) {
    return String(a.title || '').localeCompare(String(b.title || ''), 'es', {sensitivity: 'base'});
}

/**
 * Los prompts de la agencia en el orden de la lista «Prompts para {cliente}»: los del cliente
 * primero, después los de toda la agencia, y dentro de cada grupo por uso descendente y título.
 * Los de Dashbo quedan afuera: tienen su propia sección.
 */
export function ownPromptsInOrder(prompts) {
    return (prompts || []).filter(isAgencyPrompt).sort((a, b) => (
        (OWN_SCOPE_RANK[a.scope] - OWN_SCOPE_RANK[b.scope])
        || (promptUses(b) - promptUses(a))
        || compareTitles(a, b)
    ));
}

/**
 * El prompt sugerido: el más usado de los de la AGENCIA que este cliente puede usar; si la agencia
 * no tiene ninguno, el más usado de Dashbo.
 *
 * Primero los de la agencia aunque uno de Dashbo tenga más uso: el contador de un prompt de Dashbo
 * suma el uso de TODO Dashbo (`social_map_for`), así que compararlo con el de un prompt que sólo usa
 * este equipo es comparar dos poblaciones distintas — y ganaba siempre Dashbo. Dentro de los propios
 * manda el USO, no el alcance: un AGENCY con 5 usos le gana a un CLIENT con 0, aunque en la lista el
 * CLIENT vaya primero. Sólo un empate de usos se resuelve con el orden de la lista (cliente antes que
 * agencia, después título).
 *
 * Los bloqueados quedan afuera siempre: ofrecer copiar algo que al asistente le falta un
 * ingrediente para responder es mandar al usuario a una respuesta pobre. Sin ninguno habilitado
 * devuelve `null` y la tarjeta no se dibuja.
 */
export function pickSuggestedPrompt(prompts) {
    const usable = (prompts || []).filter(isPromptEnabled);
    const own = ownPromptsInOrder(usable);
    if (own.length) return byUsesDesc(own)[0];
    return byUsesDesc(usable)[0] || null;
}

/**
 * ¿El sugerido se puede presentar como «el que más usa tu equipo»? Sólo si es de la agencia y tiene
 * uso real: un ranking de ceros es una afirmación falsa, y el uso de un prompt de Dashbo es el de
 * todo Dashbo, no el de este equipo. Si además se puede decir «con {cliente}» lo decide
 * `suggestedKickerKey`.
 */
export function isTeamFavorite(prompt) {
    return isAgencyPrompt(prompt) && promptUses(prompt) > 0;
}

/**
 * Las tres lecturas de la biblioteca que dibuja la banda: el sugerido, la lista propia y la de
 * Dashbo. **El sugerido sale de las dos listas**: es el que ya está a la vista arriba, y mostrarlo
 * dos veces era el defecto que este orden vino a sacar (el mismo prompt en la tarjeta, en «Los 3
 * más usados» y en la grilla). `hasOwn` mira la lista entera, sugerido incluido: decide si Dashbo
 * abre desplegado.
 */
export function splitBandLibrary(prompts) {
    const list = prompts || [];
    const suggested = pickSuggestedPrompt(list);
    const notSuggested = (prompt) => !suggested || prompt.id !== suggested.id;
    return {
        suggested,
        own: ownPromptsInOrder(list).filter(notSuggested),
        dashbo: list.filter((prompt) => !isAgencyPrompt(prompt) && notSuggested(prompt)),
        hasOwn: list.some(isAgencyPrompt),
    };
}

/**
 * Ping de uso. Fire-and-forget de verdad: ni el rechazo de la promesa ni una excepción sincrónica
 * del cliente HTTP pueden llegar al usuario, porque para cuando esto corre **la copia ya ocurrió**.
 * Un error acá sólo significa que el contador de uso pierde un punto.
 */
export function pingPromptUsed(promptId, clientId) {
    if (!promptId) return;
    try {
        const call = new APIClient(`${API_URL}agent/prompt-library/${promptId}/used/`)
            .call('POST', clientId ? {agency_client_id: clientId} : {});
        if (call && typeof call.catch === 'function') call.catch(() => {});
    } catch (e) {
        /* el ping nunca puede tumbar una copia que ya está en el portapapeles */
    }
}

/**
 * Qué va al portapapeles según el botón: la frase del pedido o el texto del prompt. El pedido lleva
 * ESTE cliente (`client`: `{id, name}`) cuando el prompt lo necesita (`askPinsClient`): un prompt de
 * toda la agencia pedido sin cliente le llega a la IA con «{cliente}» sin completar. El texto ya
 * viene resuelto con su nombre (`resolvePromptPlaceholders` en `BoosterUseIaBand`).
 */
export function copiedTextFor(prompt, kind, client = null) {
    if (kind !== COPY_ASK) return prompt.prompt_text || '';
    return promptAskPhrase(prompt.title, askPinsClient(prompt) ? client : null);
}

/**
 * Copiar y registrar el uso. Resuelve cuando la COPIA terminó; el ping va después y no participa
 * del resultado. Rechaza sólo si el clipboard falló, que es el único caso en el que el usuario
 * tiene algo que hacer (copiar a mano).
 *
 * **Sólo «Copiar el texto» pinguea** (decisión de fede, 26/09, C-banda-02 de la tanda): el pedido
 * se usa cuando la IA trae el prompt por el MCP, y ESE fetch ya registra el uso (con el cliente
 * resuelto). Pinguear también al copiar la frase contaba dos usos por cada pedido, y ese número
 * elige el sugerido y ordena la lista — sin forma de separarlos después, porque el ping no dice qué
 * se copió. Mismo criterio en la pantalla de la agencia (`/agency/prompts`).
 *
 * El copiado va por `copyPlainText`, la MISMA función que usa la hoja "Como lo ve tu IA": tiene el
 * fallback a `execCommand` para los contextos donde `navigator.clipboard` no existe (HTTP sin TLS,
 * navegadores viejos). Llamar a `navigator.clipboard.writeText` derecho tiraba **sincrónicamente**
 * ahí adentro del onClick — o sea ni copiaba ni encendía el estado de error.
 *
 * `client` es `{id, name}`: el id va al ping, y los dos al pedido.
 */
export function copyPromptWithPing(prompt, client, kind = COPY_TEXT) {
    if (!prompt) return Promise.reject(new Error('no prompt'));
    return copyPlainText(copiedTextFor(prompt, kind, client)).then(() => {
        if (kind === COPY_TEXT) pingPromptUsed(prompt.id, client ? client.id : null);
        return true;
    });
}

/**
 * Hook del estado «copiado»: qué botón de qué prompt se acaba de copiar y, si el clipboard falló,
 * QUÉ texto no se pudo copiar. La marca es por prompt Y por botón: con dos botones por prompt, un
 * «¡Copiado!» por id encendía los dos. El timer se limpia al desmontar — sin eso, salir de la
 * pantalla dentro de los 2s deja un `setState` sobre un componente muerto.
 *
 * `failedText` es el texto y no un booleano porque el aviso de error le pide al usuario que lo
 * copie a mano, y ni la frase del pedido ni el texto de una fila están a la vista: sin mostrarlo,
 * el aviso pide algo que la pantalla no permite (`CopyFallback`).
 *
 * Un pedido copiado se muestra además en un aviso, con la frase debajo: no está escrita en la fila,
 * y es la única forma de ver que viaja con ESTE cliente. El mismo aviso que la pantalla «Prompts»
 * (`askPhraseToastContent`). El texto no: son párrafos, no una frase para revisar de un vistazo.
 */
export function useCopyState() {
    const {t} = useTranslation();
    const [copiedKey, setCopiedKey] = useState(null);
    const [failedText, setFailedText] = useState(null);
    const timer = useRef(null);

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
    }, []);

    const copy = useCallback((prompt, client, kind = COPY_TEXT) => copyPromptWithPing(prompt, client, kind)
        .then(() => {
            setFailedText(null);
            setCopiedKey(`${prompt.id}:${kind}`);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopiedKey(null), COPIED_MS);
            // Sin `closeOnClick`: la frase se selecciona con un clic (`user-select: all`), y ese
            // clic cerraba el aviso (review C-05). Se va solo, como todo aviso de éxito.
            if (kind === COPY_ASK) {
                toast.success(askPhraseToastContent(t('ia_booster.band.copy_ask_done'),
                    copiedTextFor(prompt, kind, client)), {closeOnClick: false});
            }
        })
        // Un estado a la vez. El «¡Copiado!» vive 2 s, así que una copia que falla DENTRO de esa
        // ventana dejaba el botón diciendo «✓ ¡Copiado!» con «No se pudo copiar…» justo debajo:
        // dos mensajes que se contradicen sobre la misma acción. Se apaga el anterior y se cancela
        // su timer, o el `setCopiedKey(null)` pendiente pisaría un estado que ya no es el suyo.
        .catch(() => {
            if (timer.current) clearTimeout(timer.current);
            setCopiedKey(null);
            setFailedText(prompt ? copiedTextFor(prompt, kind, client) : '');
        }), [t]);

    const isCopied = useCallback((prompt, kind) => (
        !!prompt && copiedKey === `${prompt.id}:${kind}`), [copiedKey]);

    return {isCopied, failed: failedText !== null, failedText, copy};
}

/**
 * El kicker del sugerido. «El que más usa tu equipo con {cliente}» sólo es EXACTO para un prompt
 * CLIENT: el uso de un prompt de toda la agencia se cuenta en todos sus clientes (`social_map_for`),
 * así que con un AGENCY la frase pierde el «con {cliente}» — si no, una agencia que usó 30 veces
 * «Resumen del lunes» con otros clientes lo vería presentado como lo que más usa con éste, donde se
 * usó 0. Sin uso real, o si es de Dashbo, el neutro.
 */
export function suggestedKickerKey(prompt) {
    if (!isTeamFavorite(prompt)) return 'ia_booster.band.panel_kicker';
    return prompt.scope === 'CLIENT'
        ? 'ia_booster.band.band_suggested_kicker'
        : 'ia_booster.band.band_suggested_kicker_agency';
}

/**
 * Por qué un prompt bloqueado no se ofrece: lo que le falta a ESTE cliente. Los ingredientes se
 * nombran con las mismas etiquetas que el inventario de la banda.
 */
function LockedReason({prompt}) {
    const {t} = useTranslation();
    const missing = (prompt.lock && prompt.lock.missing) || [];
    const ingredients = missing.map((ing) => t(`prompt_library.ingredients.${ing}`, ing)).join(', ');
    return (
        <span className="iab-locked">
            <Lock size={12} weight="bold"/>
            {t('ia_booster.band.band_locked', {ingredients, interpolation: {escapeValue: false}})}
        </span>
    );
}

/**
 * El botón de un copiado, con su «¡Copiado!» propio. `variant` es la clase del botón.
 *
 * Accesibilidad: el nombre del botón es el rótulo visible («Copiar pedido»), y `describedBy` apunta
 * al título del prompt — una lista con N botones iguales no dice, leída en voz alta, de cuál es cada
 * uno. El «¡Copiado!» se anuncia por una región `status`, que tiene que existir ANTES del cambio
 * (una región que aparece con el texto no se anuncia).
 */
function CopyButton({prompt, client, kind, label, variant, copyState, describedBy}) {
    const {t} = useTranslation();
    const copied = copyState.isCopied(prompt, kind);
    return (
        <>
            <button type="button" className={variant} aria-describedby={describedBy}
                    onClick={() => copyState.copy(prompt, client, kind)}>
                {copied && <Check size={12} weight="bold"/>}
                {t(copied ? 'ia_booster.band.copied' : label)}
            </button>
            <span className="visually-hidden" role="status">{copied ? t('ia_booster.band.copied') : ''}</span>
        </>
    );
}

/**
 * Ajusta el alto de un `textarea` a lo que su texto ocupa AL ANCHO REAL, con el tope que ponga el
 * CSS (`max-height`). Contar caracteres no sirve: la misma frase entra en una línea a 1440 y en dos
 * a 390 (QA-VIS-banda-16 midió las dos cosas: renglones vacíos en desktop, texto cortado en el
 * teléfono). Se recalcula cuando cambia el ancho, no el alto — si no, fijar el alto dispararía otra
 * medición.
 */
function useFitToContent(ref, text) {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const fit = () => {
            el.style.height = 'auto';
            if (!el.scrollHeight) return; // sin layout (oculto, o jsdom): queda en su `rows`
            const cs = window.getComputedStyle(el);
            const borders = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
            el.style.height = `${el.scrollHeight + borders}px`;
        };
        fit();
        if (typeof ResizeObserver === 'undefined') return undefined;
        let lastWidth = el.clientWidth;
        const observer = new ResizeObserver(() => {
            if (el.clientWidth === lastWidth) return;
            lastWidth = el.clientWidth;
            fit();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, text]);
}

/**
 * El aviso de que el portapapeles falló, CON el texto a la vista para copiarlo a mano: el aviso dice
 * «selecciona el texto», y la frase del pedido o el prompt de una fila no se ven en ningún lado. Se
 * selecciona entero al enfocarlo.
 */
function CopyFallback({text}) {
    const {t} = useTranslation();
    const ref = useRef(null);
    useFitToContent(ref, text);
    if (text === null || text === undefined) return null;
    return (
        <div className="iab-copy-fallback" role="alert">
            <p className="iab-pc-error">{t('ia_booster.band.copy_error')}</p>
            <textarea ref={ref} readOnly value={text} rows={1}
                      aria-label={t('ia_booster.band.copy_error')}
                      onFocus={(e) => e.target.select()}/>
        </div>
    );
}

export function PromptPanelCard({prompt, clientId, clientName, connected = false}) {
    const {t} = useTranslation();
    const copyState = useCopyState();

    if (!prompt) return null;

    const client = clientName || t('ia_booster.this_client');
    const askClient = {id: clientId, name: clientName};
    const titleId = `iab-pc-title-${prompt.id}`;
    // Por nombre se piden los de la AGENCIA (decisión de fede, 26/09, C-banda-06): un sugerido de
    // Dashbo —agencia sin prompts propios— ofrece sólo el texto, igual que su tarjeta en la grilla y
    // que la galería (contrato §8). Sin eso, el mismo prompt tenía acciones distintas según dónde.
    const askable = isAgencyPrompt(prompt);

    return (
        <article className="iab-pc">
            {/* La barra simula el panel del cliente con el conector andando: sólo se dibuja si la
                banda YA SABE que la IA está conectada (el mismo dato que la caja «Tu IA ya está
                conectada»). Sin conexión, o sin saberlo, afirmaría algo que no es. */}
            {connected && (
                <div className="iab-pc-top">
                    <span className="iab-pc-dot" aria-hidden="true"/>
                    <span className="iab-pc-top-text">
                        {t('ia_booster.band.panel_top', {client, interpolation: {escapeValue: false}})}
                    </span>
                    <span className="iab-pc-mini">{t('ia_booster.band.panel_mini')}</span>
                </div>
            )}
            <div className="iab-pc-body">
                <span className="iab-pc-k">
                    {t(suggestedKickerKey(prompt), {client, interpolation: {escapeValue: false}})}
                </span>
                <h3 className="iab-pc-title" id={titleId}>{prompt.title}</h3>
                {/* El anticipo de valor si el prompt lo trae; si no, el texto — nunca queda un
                    cuerpo vacío. */}
                <p className="iab-pc-p">{prompt.value_preview || prompt.prompt_text}</p>
                <div className="iab-pc-acts">
                    {askable ? (
                        <>
                            <CopyButton prompt={prompt} client={askClient} kind={COPY_ASK} copyState={copyState}
                                        label="ia_booster.band.band_copy_ask" variant="iab-btn -ia"
                                        describedBy={titleId}/>
                            <CopyButton prompt={prompt} client={askClient} kind={COPY_TEXT} copyState={copyState}
                                        label="ia_booster.band.band_copy_text" variant="iab-link"
                                        describedBy={titleId}/>
                        </>
                    ) : (
                        <CopyButton prompt={prompt} client={askClient} kind={COPY_TEXT} copyState={copyState}
                                    label="ia_booster.band.band_copy_text" variant="iab-btn -ia"
                                    describedBy={titleId}/>
                    )}
                </div>
                <CopyFallback text={copyState.failedText}/>
            </div>
        </article>
    );
}

/**
 * Una fila de la lista propia: nombre, para qué clientes es y los dos copiados. Es una FILA y no
 * una tarjeta a propósito: lo propio se recorre por nombre, que es lo que se le dice a la IA.
 */
export function PromptOwnRow({prompt, clientId, clientName}) {
    const {t} = useTranslation();
    const copyState = useCopyState();
    const enabled = isPromptEnabled(prompt);
    const onlyThisClient = prompt.scope === 'CLIENT';
    const client = clientName || t('ia_booster.this_client');
    const askClient = {id: clientId, name: clientName};
    const titleId = `iab-own-name-${prompt.id}`;

    return (
        <li className={`iab-own-row ${enabled ? '' : '-locked'}`}>
            <span className="iab-own-name" id={titleId}>{prompt.title}</span>
            <span className={`iab-scope ${onlyThisClient ? '-client' : '-all'}`}>
                {onlyThisClient
                    ? t('ia_booster.band.band_scope_client', {client, interpolation: {escapeValue: false}})
                    : t('ia_booster.band.band_scope_all')}
            </span>
            <span className="iab-own-acts">
                {enabled ? (
                    <>
                        <CopyButton prompt={prompt} client={askClient} kind={COPY_TEXT} copyState={copyState}
                                    label="ia_booster.band.band_copy_text" variant="iab-link"
                                    describedBy={titleId}/>
                        <CopyButton prompt={prompt} client={askClient} kind={COPY_ASK} copyState={copyState}
                                    label="ia_booster.band.band_copy_ask" variant="iab-btn -ghost"
                                    describedBy={titleId}/>
                    </>
                ) : <LockedReason prompt={prompt}/>}
            </span>
            <CopyFallback text={copyState.failedText}/>
        </li>
    );
}

/**
 * La tarjeta de un prompt de Dashbo, dentro del plegado. Sólo «Copiar el texto»: son genéricos
 * para empezar, y lo que se pide por nombre es lo de la agencia (para adaptarlos está «Prompts»).
 */
export function PromptMiniCard({prompt, clientId}) {
    const {t} = useTranslation();
    const copyState = useCopyState();
    const enabled = isPromptEnabled(prompt);
    const uses = promptUses(prompt);
    const askClient = {id: clientId}; // sólo copia el texto: el cliente va al ping de uso
    const titleId = `iab-jcard-title-${prompt.id}`;

    return (
        <article className={`iab-jcard ${enabled ? '' : '-locked'}`}>
            <div className="iab-jcard-tags">
                {prompt.role && <span className="iab-tag">{t(`prompt_library.roles.${prompt.role}`)}</span>}
                {(prompt.momentos || []).map((m) => (
                    <span key={m} className="iab-tag">{t(`prompt_library.momentos.${m}`)}</span>
                ))}
            </div>
            <h4 className="iab-jcard-title" id={titleId}>{prompt.title}</h4>
            {prompt.value_preview && <p className="iab-jcard-p">{prompt.value_preview}</p>}
            <div className="iab-jcard-acts">
                {enabled ? (
                    <CopyButton prompt={prompt} client={askClient} kind={COPY_TEXT} copyState={copyState}
                                label="ia_booster.band.band_copy_text" variant="iab-btn -ghost"
                                describedBy={titleId}/>
                ) : <LockedReason prompt={prompt}/>}
                <span className="iab-jcard-spacer"/>
                {/* El contador sólo aparece cuando hay uso real: un «0 usos» pegado a cada card
                    convierte una biblioteca nueva en un cementerio. */}
                {uses > 0 && <span className="iab-tag">{t('prompt_library.uses', {count: uses})}</span>}
            </div>
            <CopyFallback text={copyState.failedText}/>
        </article>
    );
}

export default PromptPanelCard;
